import { Button, Menu, TextInput } from '@mantine/core';
import { IconChevronDown, IconSearch } from '@tabler/icons-react';
import type { Table, Column } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';

export default function DataTableFilterControl<TData>({ table }: { table: Table<TData> }) {
  const allRows = table.getCoreRowModel().rows;

  const filterableColumns = table.getAllColumns().filter((column) => Boolean(column.columnDef.enableColumnFilter));

  // Memoize all column unique values as a map of column.id -> unique values
  const columnUniqueValuesMap = useMemo(() => {
    const map = new Map<string, string[]>();

    filterableColumns.forEach((column) => {
      if (!column) {
        return;
      }

      const uniqueValues = [
        ...new Set(
          allRows.flatMap((row) => {
            const value = row.getValue(column.id);
            // Handle array values such as test suites
            if (Array.isArray(value)) {
              return value.map((item) => item?.name).filter(Boolean);
            }
            return value;
          }),
        ),
      ].filter(Boolean) as string[];

      map.set(column.id, uniqueValues);
    });

    return map;
  }, [allRows, filterableColumns]);

  const getColumnUniqueValues = (column: Column<TData>) => {
    if (!column) {
      return [];
    }
    return columnUniqueValuesMap.get(column.id) || [];
  };

  // Use the column's meta.filterDisplayValue function if available
  const getDisplayValue = (column: Column<TData>, value: string): React.ReactNode => {
    if (column.columnDef.meta?.filterDisplayValue) {
      const { filterDisplayValue } = column.columnDef.meta;
      return filterDisplayValue[value] || value;
    }
    return value;
  };

  const { globalFilter } = table.getState();

  const showGlobalFilter = globalFilter !== '';
  const showClearFilters = useMemo(
    () => filterableColumns.some((column) => column.getFilterValue()) || showGlobalFilter,
    [filterableColumns, showGlobalFilter],
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      {filterableColumns.map((column) => {
        const uniqueValues = getColumnUniqueValues(column);
        const filterValue = column.getFilterValue() as string | undefined;
        return (
          <FilterMenu
            key={column.id}
            column={column}
            uniqueValues={uniqueValues}
            filterValue={filterValue}
            getDisplayValue={getDisplayValue}
          />
        );
      })}
      {!!showGlobalFilter && (
        <Button
          variant="default"
          size="compact-sm"
          className="pointer-events-none text-sm"
        >
          <div>
            <span>Showing results for </span>
            <span className="text-blue-500">&quot;{globalFilter}&quot;</span>
          </div>
        </Button>
      )}
      {!!showClearFilters && (
        <Button
          variant="subtle"
          size="compact-sm"
          color="gray"
          onClick={() => {
            table.resetColumnFilters();
            table.setGlobalFilter('');
          }}
        >
          Clear Filters
        </Button>
      )}
    </div>
  );
}

function FilterMenu<TData>({
  column,
  uniqueValues,
  filterValue,
  getDisplayValue,
}: {
  column: Column<TData>;
  uniqueValues: string[];
  filterValue: string | undefined;
  getDisplayValue: (column: Column<TData>, value: string) => React.ReactNode;
}) {
  const [search, setSearch] = useState('');

  // Filter by display value so users can search by what they see in the dropdown
  const filteredValues = useMemo(() => {
    const sorted = [...uniqueValues].sort((a, b) => a.localeCompare(b));
    if (!search) {
      return sorted;
    }
    const lowerSearch = search.toLowerCase();
    return sorted.filter((value) => {
      const displayValue = getDisplayValue(column, value);
      const displayString = typeof displayValue === 'string' ? displayValue : value;
      return displayString.toLowerCase().includes(lowerSearch);
    });
  }, [uniqueValues, search, column, getDisplayValue]);

  // Reset search when menu closes
  const handleMenuChange = (opened: boolean) => {
    if (!opened) {
      setSearch('');
    }
  };

  return (
    <Menu
      position="bottom-start"
      onChange={handleMenuChange}
    >
      <Menu.Target>
        <Button
          variant="default"
          size="compact-sm"
          rightSection={<IconChevronDown size={14} />}
        >
          <div className="flex items-center gap-1">
            {typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id}
            <span className="text-blue-500">{filterValue ? getDisplayValue(column, filterValue) : null}</span>
          </div>
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <div className="sticky top-0 z-10 bg-white p-1 dark:bg-stone-800">
          <TextInput
            placeholder="Search..."
            size="xs"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>
        <div className="max-h-64 overflow-y-auto">
          <Menu.Item
            onClick={() => column.setFilterValue(undefined)}
            color={!filterValue ? 'blue' : undefined}
          >
            All
          </Menu.Item>
          {filteredValues.map((value: string) => (
            <Menu.Item
              key={value}
              onClick={() => {
                if (filterValue !== value) {
                  column.setFilterValue(value);
                }
              }}
              color={filterValue === value ? 'blue' : undefined}
            >
              {getDisplayValue(column, value)}
            </Menu.Item>
          ))}
          {filteredValues.length === 0 && search && (
            <div className="px-3 py-2 text-sm text-stone-500">No results found</div>
          )}
        </div>
      </Menu.Dropdown>
    </Menu>
  );
}
